import {
  domAnimation,
  LazyMotion,
  m,
  useScroll,
  useTransform,
} from "framer-motion";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

type TimelineEntry = {
  title: string;
  content: ReactNode;
};

type TimelineProps = {
  data: TimelineEntry[];
};

export const Timeline = ({ data }: TimelineProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: should only run once
  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setHeight(rect.height);
    }

    // Update height on resize
    const handleResize = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setHeight(rect.height);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [ref]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 50%"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <LazyMotion features={domAnimation} strict>
      <div
        className="w-full bg-white font-sans dark:bg-neutral-950"
        ref={containerRef}
      >
        <div
          className="relative mx-auto max-w-7xl px-4 pb-12 md:px-8 md:pb-20"
          ref={ref}
        >
          {data.map((item) => (
            <div
              className="flex justify-start pt-8 md:gap-6 md:pt-20 lg:gap-10 lg:pt-40"
              key={item.title}
            >
              <div className="sticky top-20 z-40 flex max-w-xs flex-col items-center self-start md:top-40 md:w-full md:flex-row lg:max-w-sm">
                <div className="absolute left-3 flex h-8 w-8 items-center justify-center rounded-full bg-white md:left-3 md:h-10 md:w-10 dark:bg-black">
                  <div className="h-3 w-3 rounded-full border border-neutral-300 bg-neutral-200 p-2 md:h-4 md:w-4 dark:border-neutral-700 dark:bg-neutral-800" />
                </div>
                <h3 className="hidden break-words font-medium text-base text-muted-foreground md:block md:pl-16 md:text-lg lg:pl-20 lg:text-xl xl:text-2xl">
                  {item.title}
                </h3>
              </div>

              <div className="relative w-full min-w-0 pr-2 pl-12 md:pr-4 md:pl-16 lg:pl-20">
                <h3 className="mb-4 block text-left font-medium text-lg text-muted-foreground md:hidden">
                  {item.title}
                </h3>
                <div className="w-full overflow-hidden">{item.content}</div>
              </div>
            </div>
          ))}
          <div
            className="mask-[linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)] absolute top-0 left-3 w-[2px] overflow-hidden bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-0% from-transparent via-neutral-200 to-99% to-transparent md:left-8 dark:via-neutral-700"
            style={{
              height: `${height}px`,
            }}
          >
            <m.div
              className="absolute inset-x-0 top-0 w-[2px] rounded-full bg-linear-to-t from-0% from-purple-500 via-10% via-blue-500 to-transparent"
              style={{
                height: heightTransform,
                opacity: opacityTransform,
              }}
            />
          </div>
        </div>
      </div>
    </LazyMotion>
  );
};
